import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Button } from '@mui/material';
import React from 'react';
import { Loading, useNotify } from 'react-admin';

import {
  AdminClearClientLaunchItemDocument,
  AdminClientLaunchDocument,
  AdminConfirmClientLaunchItemDocument,
  AdminFinishClientLaunchDocument,
  AdminSaveClientLaunchStepDocument,
  AdminStartClientLaunchDocument,
  ClientLaunchStatus,
  ClientLaunchType,
  type ConfigurationIssue,
} from '../../../graphql/types';
import useCanWrite from '../../../hooks/useCanWrite';
import { monthDayYear } from '../../../utils/date';
import { Checkpoint } from './Checkpoint';
import { LaunchLayout } from './LaunchLayout';
import { DiscardDialog, useLeaveGuard } from './LeaveGuard';
import {
  activeItemIds,
  getBlockedReason,
  getContinueLabel,
  getDiagnosticsState,
  getInitialStepIndex,
  getLaunchTitle,
  getSavedChoices,
  getStepStatuses,
  isReadOnly,
  pickStepData,
  REVIEW_STEP_ID,
  withoutConfirmations,
} from './launchState';
import { SidePanel } from './SidePanel';
import { getLaunchSteps } from './steps';
import type {
  AutoCheckContext,
  ClientLaunch,
  CreateClient,
  DiagnosticsResult,
  SetupChoices,
  StepSave,
  StepStatus,
} from './types';

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const addTo = (set: ReadonlySet<string>, id: string) => new Set(set).add(id);

const removeFrom = (set: ReadonlySet<string>, id: string) => {
  const next = new Set(set);
  next.delete(id);
  return next;
};

type ChoiceDrafts = Record<string, Partial<SetupChoices>>;

const mergeChoices = (launch: ClientLaunch, drafts: ChoiceDrafts) =>
  Object.values(drafts).reduce<SetupChoices>(
    (choices, draft) => ({ ...choices, ...draft }),
    getSavedChoices(launch),
  );

const omitStep = (drafts: ChoiceDrafts, stepId: string) => {
  const { [stepId]: _discarded, ...rest } = drafts;
  return rest;
};

const ReadOnlyNote: React.FC<{ launch: ClientLaunch }> = ({ launch }) => {
  const noun =
    launch.type === ClientLaunchType.FeatureLaunch ? 'launch' : 'setup';
  if (!isReadOnly(launch)) {
    return (
      <Alert severity="info" icon={false}>
        Your account has read-only access, so you can view this {noun} but not
        change it.
      </Alert>
    );
  }
  const verb =
    launch.status === ClientLaunchStatus.Abandoned ? 'abandoned' : 'finished';
  const date = monthDayYear(launch.finishedAt ?? launch.updatedAt);
  const owner = launch.owner
    ? ` by ${launch.owner.firstName} ${launch.owner.lastName}`
    : '';
  return (
    <Alert severity="info" icon={false}>
      This {noun} was {verb} {date}
      {owner}. It's read only. Make changes from the client page.
    </Alert>
  );
};

export const LaunchFlow: React.FC<{ launch: ClientLaunch }> = ({ launch }) => {
  const notify = useNotify();
  const steps = getLaunchSteps(launch.type);
  const canWrite = useCanWrite();
  const readOnly = isReadOnly(launch) || !canWrite;

  const [dirtySteps, setDirtySteps] = React.useState<ReadonlySet<string>>(
    new Set(),
  );
  const [resetSteps, setResetSteps] = React.useState<ReadonlySet<string>>(
    new Set(),
  );
  // Bumped on every edit, so a diagnostics run from before it goes stale.
  const [editVersion, setEditVersion] = React.useState(0);
  const [diagnosticsResult, setDiagnosticsResult] =
    React.useState<DiagnosticsResult>();
  const [busy, setBusy] = React.useState(false);
  // Input on the current step that Save & continue hasn't saved yet. Switching
  // steps unmounts the step, so it's lost unless the user saves first.
  const [hasInput, setHasInput] = React.useState(false);
  const [pendingIndex, setPendingIndex] = React.useState<number>();
  // Steps edited after confirming whose confirmations aren't cleared yet, or
  // failed to clear. They count as unconfirmed until clearing succeeds.
  const [unclearedSteps, setUnclearedSteps] = React.useState<
    ReadonlySet<string>
  >(new Set());
  // Choices each step changed since it was last saved.
  const [choiceDrafts, setChoiceDrafts] = React.useState<ChoiceDrafts>({});
  const saveHandlers = React.useRef<Record<string, StepSave | undefined>>({});
  const clearing = React.useRef<Record<string, Promise<boolean> | undefined>>(
    {},
  );

  const [saveStep] = useMutation(AdminSaveClientLaunchStepDocument);
  const [confirmItem] = useMutation(AdminConfirmClientLaunchItemDocument);
  const [clearItem] = useMutation(AdminClearClientLaunchItemDocument);
  const [finishLaunch] = useMutation(AdminFinishClientLaunchDocument);

  const diagnostics = getDiagnosticsState(diagnosticsResult, editVersion);
  const context: AutoCheckContext = {
    launch,
    // Runs aren't stored, but finishing needed a clean one. An abandoned
    // launch may never have had one.
    diagnosticsClean:
      launch.status === ClientLaunchStatus.Finished ||
      (diagnostics.kind === 'done' && !diagnostics.issues.length),
  };
  const shownLaunch = withoutConfirmations(launch, unclearedSteps);
  const choices = mergeChoices(launch, choiceDrafts);
  const statuses = getStepStatuses(
    steps,
    shownLaunch,
    choices,
    dirtySteps,
    context,
  );

  const [currentIndex, setCurrentIndex] = React.useState(() =>
    getInitialStepIndex(steps, statuses, launch),
  );
  const step = steps[currentIndex];
  const status = statuses[currentIndex];
  const items = step.checkpoint(launch, choices);
  const confirmed = activeItemIds(shownLaunch, step.id);
  const blockedReason = getBlockedReason(currentIndex, statuses);
  const leaveGuard = useLeaveGuard(hasInput);

  const goTo = (index: number) => {
    setHasInput(false);
    setCurrentIndex(index);
    window.scrollTo({ top: 0 });
  };

  const requestStep = (index: number) => {
    // A save in flight moves on when it's done.
    if (index === currentIndex || busy) {
      return;
    }
    if (hasInput) {
      setPendingIndex(index);
    } else {
      goTo(index);
    }
  };

  // The step shows its saved values again, so it's no longer edited.
  const discardInput = () => {
    setDirtySteps((current) => removeFrom(current, step.id));
    setChoiceDrafts((current) => omitStep(current, step.id));
    setHasInput(false);
  };

  const handleDiscard = () => {
    discardInput();
    if (pendingIndex !== undefined) {
      setPendingIndex(undefined);
      goTo(pendingIndex);
    }
  };

  const handleLeave = () => {
    discardInput();
    leaveGuard.leave();
  };

  const goToStep = (stepId: string) => {
    const index = steps.findIndex((s) => s.id === stepId);
    if (index >= 0 && (readOnly || statuses[index].reachable)) {
      requestStep(index);
    }
  };

  // Resolves with whether the step's confirmations are cleared. A clear already
  // in flight is shared, not sent twice.
  const clearStep = (stepId: string) => {
    const inFlight = clearing.current[stepId];
    if (inFlight) {
      return inFlight;
    }
    setUnclearedSteps((current) => addTo(current, stepId));
    setResetSteps((current) => addTo(current, stepId));
    const request = clearItem({
      variables: { input: { id: launch.id, stepId } },
    })
      .then(() => {
        setUnclearedSteps((current) => removeFrom(current, stepId));
        return true;
      })
      .catch((error: unknown) => {
        notify(
          `Failed to clear confirmations: ${errorMessage(error)}. Edit the step or tick an item to try again.`,
          { type: 'error' },
        );
        return false;
      })
      .finally(() => {
        clearing.current[stepId] = undefined;
      });
    clearing.current[stepId] = request;
    return request;
  };

  const handleEdit = () => {
    if (readOnly) {
      return;
    }
    setEditVersion((version) => version + 1);
    setHasInput(true);
    if (status.saved) {
      setDirtySteps((current) => addTo(current, step.id));
    }
    if (activeItemIds(launch, step.id).size) {
      clearStep(step.id);
    }
  };

  const handleToggle = async (itemId: string, checked: boolean) => {
    const input = { id: launch.id, stepId: step.id, itemId };
    if (unclearedSteps.has(step.id) && !(await clearStep(step.id))) {
      return;
    }
    setResetSteps((current) => removeFrom(current, step.id));
    try {
      if (checked) {
        await confirmItem({ variables: { input } });
      } else {
        await clearItem({ variables: { input } });
      }
    } catch (error) {
      notify(`Failed to update the checkpoint: ${errorMessage(error)}`, {
        type: 'error',
      });
    }
  };

  const handleContinue = async () => {
    if (blockedReason) {
      return;
    }
    if (step.id !== REVIEW_STEP_ID && status.saved && !status.dirty) {
      goTo(currentIndex + 1);
      return;
    }
    setBusy(true);
    try {
      if (step.id === REVIEW_STEP_ID) {
        await finishLaunch({ variables: { id: launch.id } });
        notify(
          launch.type === ClientLaunchType.FeatureLaunch
            ? 'Launch finished'
            : 'Setup finished',
          { type: 'success' },
        );
        return;
      }
      await saveHandlers.current[step.id]?.();
      const nextStep = steps[currentIndex + 1];
      const data = pickStepData(step, choices);
      await saveStep({
        variables: {
          input: {
            id: launch.id,
            stepId: step.id,
            nextStepId: nextStep.id,
            ...(data && { data }),
          },
        },
      });
      // The saved launch carries them now.
      setChoiceDrafts((current) => omitStep(current, step.id));
      setDirtySteps((current) => removeFrom(current, step.id));
      goTo(currentIndex + 1);
    } catch (error) {
      notify(`Failed to save: ${errorMessage(error)}`, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const registerSave = React.useCallback(
    (save: StepSave | undefined) => {
      saveHandlers.current[step.id] = save;
    },
    [step.id],
  );

  const handleChoicesChange = React.useCallback(
    (patch: Partial<SetupChoices>) =>
      setChoiceDrafts((current) => ({
        ...current,
        [step.id]: { ...current[step.id], ...patch },
      })),
    [step.id],
  );

  const handleDiagnosticsStart = () => setDiagnosticsResult(undefined);

  const handleDiagnosticsRun = (issues: ConfigurationIssue[]) =>
    setDiagnosticsResult({ issues, editVersion });

  return (
    <LaunchLayout
      title={getLaunchTitle(launch)}
      steps={steps}
      statuses={statuses}
      currentIndex={currentIndex}
      readOnly={readOnly}
      onSelect={requestStep}
      note={readOnly ? 'Read only' : blockedReason}
      actions={
        <>
          <Button
            disabled={!currentIndex || busy}
            onClick={() => requestStep(currentIndex - 1)}
          >
            {readOnly ? 'Previous step' : 'Back'}
          </Button>
          {readOnly ? (
            <Button
              variant="outlined"
              disabled={currentIndex === steps.length - 1}
              onClick={() => goTo(currentIndex + 1)}
            >
              Next step
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={busy || Boolean(blockedReason)}
              onClick={handleContinue}
            >
              {getContinueLabel(step, status, launch.type)}
            </Button>
          )}
        </>
      }
      aside={
        <SidePanel
          launch={launch}
          choices={choices}
          steps={steps}
          statuses={statuses}
          readOnly={readOnly}
        />
      }
    >
      {readOnly && <ReadOnlyNote launch={launch} />}
      {/* Disabled while saving too, so nothing typed then is dropped. */}
      <Box
        component="fieldset"
        disabled={readOnly || busy}
        sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
      >
        <step.Component
          key={step.id}
          launch={launch}
          readOnly={readOnly}
          saving={busy}
          choices={choices}
          onChoicesChange={handleChoicesChange}
          onEdit={handleEdit}
          registerSave={registerSave}
          steps={steps}
          statuses={statuses}
          goToStep={goToStep}
          diagnostics={diagnostics}
          onDiagnosticsStart={handleDiagnosticsStart}
          onDiagnosticsRun={handleDiagnosticsRun}
        />
      </Box>
      {items.length > 0 && (
        <Checkpoint
          items={items}
          confirmed={confirmed}
          context={context}
          wasReset={resetSteps.has(step.id)}
          disabled={readOnly || busy}
          onToggle={handleToggle}
        />
      )}
      <DiscardDialog
        open={pendingIndex !== undefined || leaveGuard.blocked}
        stepTitle={step.title}
        onDiscard={leaveGuard.blocked ? handleLeave : handleDiscard}
        onKeep={() => {
          setPendingIndex(undefined);
          leaveGuard.stay();
        }}
      />
    </LaunchLayout>
  );
};

/**
 * A new client before step 1 is saved: there's no client or launch yet, so
 * only step 1 can be reached. Continuing creates the client, starts the launch
 * and records step 1, then hands over the launch ID.
 */
export const NewClientFlow: React.FC<{
  onStarted: (launchId: string) => void;
}> = ({ onStarted }) => {
  const notify = useNotify();
  const steps = getLaunchSteps(ClientLaunchType.NewClient);
  const [step, nextStep] = steps;
  const statuses = steps.map(
    (s, index): StepStatus => ({
      id: s.id,
      saved: false,
      dirty: false,
      confirmedCount: 0,
      itemCount: 0,
      checkpointDone: true,
      complete: false,
      reachable: index === 0,
    }),
  );
  const [busy, setBusy] = React.useState(false);
  const [hasInput, setHasInput] = React.useState(false);
  const leaveGuard = useLeaveGuard(hasInput);
  // Kept so a retry doesn't create a second client, or start a second launch,
  // which the backend rejects while the first is in progress.
  const [clientId, setClientId] = React.useState<string>();
  const [launchId, setLaunchId] = React.useState<string>();
  const createClient = React.useRef<CreateClient>(undefined);

  const [startLaunch] = useMutation(AdminStartClientLaunchDocument);
  const [saveStep] = useMutation(AdminSaveClientLaunchStepDocument);

  const registerCreateClient = React.useCallback(
    (create: CreateClient | undefined) => {
      createClient.current = create;
    },
    [],
  );

  const handleContinue = async () => {
    setBusy(true);
    try {
      let id = clientId;
      if (!id) {
        if (!createClient.current) {
          throw new Error("This step can't create the client.");
        }
        id = await createClient.current();
        setClientId(id);
      }
      let started = launchId;
      if (!started) {
        const { data } = await startLaunch({
          variables: {
            input: { type: ClientLaunchType.NewClient, clientId: id },
          },
        });
        started = data?.startClientLaunch.clientLaunch.id;
        if (!started) {
          throw new Error('The launch was not started.');
        }
        setLaunchId(started);
      }
      await saveStep({
        variables: {
          input: { id: started, stepId: step.id, nextStepId: nextStep.id },
        },
      });
      leaveGuard.allowLeave();
      onStarted(started);
    } catch (error) {
      notify(`Failed to create the client: ${errorMessage(error)}`, {
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <LaunchLayout
      title="New client"
      steps={steps}
      statuses={statuses}
      currentIndex={0}
      readOnly={false}
      onSelect={() => undefined}
      actions={
        <Button variant="contained" disabled={busy} onClick={handleContinue}>
          Create client & continue
        </Button>
      }
      aside={
        <SidePanel
          launch={null}
          choices={getSavedChoices(null)}
          steps={steps}
          statuses={statuses}
          readOnly={false}
        />
      }
    >
      {clientId && (
        <Alert severity="info">
          The client was created. Continue to start its setup; you can change
          these fields there.
        </Alert>
      )}
      {/* Locked once the client exists: a retry doesn't save these again. */}
      <Box
        component="fieldset"
        disabled={busy || Boolean(clientId)}
        sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
      >
        <step.Component
          launch={null}
          readOnly={Boolean(clientId)}
          saving={busy}
          choices={getSavedChoices(null)}
          onEdit={() => setHasInput(true)}
          registerCreateClient={registerCreateClient}
          steps={steps}
          statuses={statuses}
          goToStep={() => undefined}
          diagnostics={{ kind: 'notRun' }}
          onDiagnosticsStart={() => undefined}
          onDiagnosticsRun={() => undefined}
        />
      </Box>
      <DiscardDialog
        open={leaveGuard.blocked}
        stepTitle={step.title}
        onDiscard={leaveGuard.leave}
        onKeep={leaveGuard.stay}
      />
    </LaunchLayout>
  );
};

/** Loads a launch and opens it at its saved step, or read only once closed. */
export const LaunchWizard: React.FC<{ launchId: string }> = ({ launchId }) => {
  const { data, loading, error } = useQuery(AdminClientLaunchDocument, {
    variables: { id: launchId },
  });

  if (loading && !data) {
    return <Loading />;
  }
  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  if (!data?.clientLaunch) {
    return <Alert severity="warning">This launch doesn't exist.</Alert>;
  }
  return <LaunchFlow key={launchId} launch={data.clientLaunch} />;
};
